/**
 * Trunnion AI - Notification Item Component
 * Single notification row with icon, content, timestamp, and dismiss action.
 */

import { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconColorMap = {
  success: 'text-green-400',
  error: 'text-red-400',
  warning: 'text-yellow-400',
  info: 'text-blue-400',
};

/**
 * Formats a timestamp into a human-readable relative string.
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

interface NotificationItemProps {
  notification: Notification;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const { removeFromHistory, markRead } = useNotificationStore();
  const [hovered, setHovered] = useState(false);

  const Icon = iconMap[notification.type];
  const isUnread = !notification.read;

  const handleClick = () => {
    if (isUnread) {
      markRead(notification.id);
    }
  };

  return (
    <div
      className={`group relative flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${
        hovered ? 'bg-trunnion-slate-800/60' : ''
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      role="listitem"
    >
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2">
          <div className="h-2 w-2 rounded-full bg-trunnion-gold-500" />
        </div>
      )}

      {/* Icon */}
      <div className="shrink-0 mt-0.5">
        <Icon className={`h-5 w-5 ${iconColorMap[notification.type]}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {notification.title}
        </p>
        <p className="mt-0.5 text-sm text-trunnion-slate-400 line-clamp-2">
          {notification.message}
        </p>
      </div>

      {/* Timestamp and dismiss */}
      <div className="shrink-0 flex items-center gap-1">
        <span className="text-xs text-trunnion-slate-500 whitespace-nowrap">
          {formatRelativeTime(notification.timestamp)}
        </span>
        {hovered && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeFromHistory(notification.id);
            }}
            className="rounded p-0.5 text-trunnion-slate-500 hover:text-white hover:bg-trunnion-slate-700 transition-colors"
            aria-label="Dismiss notification"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
